import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { zodResolver } from '@hookform/resolvers/zod';
import CompanyHeader from '@/components/company/CompanyHeader';
import FormBuilder, { FormField } from '@/components/company/FormBuilder';
import GenerateAIModal from '@/components/company/GenerateAIModal';
import AIResultModal from '@/components/company/AIResultModal';
import PurposeAIGenerateForm from './purpose/PurposeAIGenerateForm';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useGeneratePurpose } from './purpose/useGeneratePurpose';
import { purposeAIGenerateSchema, type PurposeAIGenerateFormData } from './purpose/purpose-ai-generate.schema';

interface PurposeFormValues {
  purpose: string;
  is_published_purpose: boolean;
}

export default function Purpose() {
  const { companyProfile, updateCompanyField, isLoading } = useCompanyState();
  const { currentTenant } = useTenantState();

  const formControl = useForm<PurposeFormValues>({
    defaultValues: {
      purpose: '',
      is_published_purpose: false,
    },
  });

  const { handleSubmit, reset, watch, setValue } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const aiFormControl = useForm<PurposeAIGenerateFormData>({
    resolver: zodResolver(purposeAIGenerateSchema),
    defaultValues: {
      company_name: '',
      industry: '',
      target_market: '',
      current_activities: '',
    },
  });

  const [fields, setFields] = useState<FormField[]>([]);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [generatedOptions, setGeneratedOptions] = useState<string[]>([]);

  const generatePurposeMutation = useGeneratePurpose({
    tenantId: currentTenant?.id ? String(currentTenant.id) : '',
  });

  useEffect(() => {
    if (!companyProfile) return;

    const purposeObj =
      typeof companyProfile.purpose === 'object' && companyProfile.purpose !== null
        ? (companyProfile.purpose as { value: string; is_published: boolean })
        : null;

    reset({
      purpose: purposeObj ? purposeObj.value : (companyProfile.purpose as string) ?? '',
      is_published_purpose: purposeObj ? purposeObj.is_published : Boolean(companyProfile.is_published_purpose),
    });
  }, [companyProfile, reset]);

  const handleSave = async (data: PurposeFormValues) => {
    if (!currentTenant?.id) return;
    updateCompanyField(currentTenant.id, 'purpose', data.purpose, data.is_published_purpose);
  };

  useEffect(() => {
    setFields([
      {
        name: 'purpose',
        label: 'Company Purpose',
        type: 'markdown',
        required: true,
        placeholder: "Your company's purpose...",
      },
    ]);
  }, []);

  const handleOpenGenerateModal = () => {
    aiFormControl.reset();
    setIsGenerateModalOpen(true);
  };

  const handleCloseGenerateModal = () => {
    setIsGenerateModalOpen(false);
  };

  const handleGenerate = async (data: PurposeAIGenerateFormData) => {
    try {
      const result = await generatePurposeMutation.mutateAsync(data);

      if (result.success && result.data?.generated_options) {
        setGeneratedOptions(result.data.generated_options);
        setIsGenerateModalOpen(false);
        setIsResultModalOpen(true);
        toast.success('Purpose generated successfully');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate purpose');
    }
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
    setGeneratedOptions([]);
  };

  const handleSaveGeneratedPurpose = (selectedOption: string) => {
    setValue('purpose', selectedOption);
    setIsResultModalOpen(false);
    setGeneratedOptions([]);
  };

  const handleAfterSave = async () => {
    if (!currentTenant?.id) return;
    const currentPurpose = formControl.getValues('purpose');
    const currentPublished = formControl.getValues('is_published_purpose');
    await updateCompanyField(currentTenant.id, 'purpose', currentPurpose, currentPublished);
    toast.success('Purpose saved successfully');
  };

  const isPublished = watch('is_published_purpose');

  return (
    <section className="w-full">
      <CompanyHeader
        title="Company Purpose"
        description="Define your company's fundamental reason for existence and core purpose."
        onGenerate={handleOpenGenerateModal}
        generateLabel="Generate"
        saveLabel="Save"
        onSave={() => formRef.current?.requestSubmit()}
        isSaving={isLoading}
      />

      <div className="mx-5 border-b border-gray-200" />

      <FormProvider {...formControl}>
        <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="pt-5">
          <FormBuilder
            fields={fields}
            initialPublished={isPublished}
            onPublishChange={v => setValue('is_published_purpose', v)}
          />
        </form>
      </FormProvider>

      <GenerateAIModal
        isOpen={isGenerateModalOpen}
        onClose={handleCloseGenerateModal}
        onSubmit={aiFormControl.handleSubmit(handleGenerate)}
        title="Generate Purpose with AI"
        description="Provide the information below to help AI generate purpose that suits your company."
        generateButtonLabel="Generate Purpose"
        isLoading={generatePurposeMutation.isLoading}
      >
        <PurposeAIGenerateForm
          control={aiFormControl.control}
          errors={aiFormControl.formState.errors}
          isLoading={generatePurposeMutation.isLoading}
        />
      </GenerateAIModal>

      <AIResultModal
        isOpen={isResultModalOpen}
        onClose={handleCloseResultModal}
        options={generatedOptions}
        onSave={handleSaveGeneratedPurpose}
        onAfterSave={handleAfterSave}
        title="AI Generate Purpose"
        description="Select the purpose that best represents your company"
        saveButtonLabel="Save to Profile"
        isLoading={isLoading}
      />
    </section>
  );
}
